// ─────────────────────────────────────────────
//  tvLayout — the TV board's responsive fit algorithm.
//
//  Goal: on ANY monitor (small office screen → large hospital wall), pick the
//  column count that gives the LARGEST cards that still fit both dimensions,
//  with every element scaling proportionally:
//    • fonts/paddings scale via the --atc-scale CSS var (resolution-based,
//      1080p = 1.0, 4K = 2.0 — tvScaleForViewport),
//    • card geometry uses DESIGN_CARD_W/H × that same scale,
//    • a final shrink-only transform (BoardPage tvScale) catches any residue,
//      so cards stay uniform and nothing ever overlaps.
//
//  Pure math — inputs are measured pixels, output is { cols, fit }.
// ─────────────────────────────────────────────

// The card's design-space footprint (at --atc-scale = 1): the fixed-layout
// card is ~402px tall (uniform-height contract) and reads comfortably down
// to ~232px wide.
export const DESIGN_CARD_W = 232;
export const DESIGN_CARD_H = 402;
export const TV_GRID_GAP = 12;

/**
 * Pick the TV column count.
 * @param {object} p
 *   @param {number} p.availW     rooms-area width in px (screen minus panels)
 *   @param {number} p.availH     rooms-area height in px
 *   @param {number} p.roomCount  cards on the board
 *   @param {number} p.scale      the resolution scale (--atc-scale)
 *   @param {number} [p.benchH]   extra height reserved under the grid (bench
 *                                sections), already in SCREEN px
 *   @param {number} [p.maxCols]
 * @returns {{ cols: number, fit: number }}  fit = min(widthRatio, heightRatio)
 *   for the chosen count (1 = cards at full design size; <1 = the safety
 *   transform will shrink; >1 capped — cards don't balloon past design+scale).
 */
export function pickTvColumns({ availW, availH, roomCount, scale = 1, benchH = 0, maxCols = 8 }) {
  const n = Math.max(0, Math.floor(roomCount));
  if (!n || !(availW > 0) || !(availH > 0)) return { cols: 4, fit: 1 };
  const cardW = DESIGN_CARD_W * scale;
  const cardH = DESIGN_CARD_H * scale;
  const usableH = Math.max(1, availH - benchH);

  let best = { cols: 1, fit: -Infinity };
  const cap = Math.min(maxCols, n);
  for (let c = 1; c <= cap; c += 1) {
    const rows = Math.ceil(n / c);
    const cellW = (availW - TV_GRID_GAP * (c - 1)) / c;
    const cellH = (usableH - TV_GRID_GAP * (rows - 1)) / rows;
    // How much of the design-size card fits in this cell (worst dimension).
    const fit = Math.min(cellW / cardW, cellH / cardH);
    if (fit > best.fit) best = { cols: c, fit };
  }
  // fit > 1 means the screen has room to spare — cards render at design size
  // (readable, consistent); we don't inflate geometry beyond the resolution
  // scale, so cap the reported fit at 1 for the safety-transform math.
  return { cols: best.cols, fit: Math.min(best.fit, 1) };
}
